import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from './LanguageContext';
import { Check, X } from 'lucide-react';
const COMPARISONS = [{
  charge: {
    en: 'Stair Charges',
    es: 'Cargo por Escaleras'
  },
  others: {
    en: '$50 – $100 extra',
    es: '$50 – $100 adicionales'
  },
  us: {
    en: 'FREE (Included)',
    es: 'GRATIS (Incluido)'
  }
}, {
  charge: {
    en: 'Diesel & Tolls',
    es: 'Diesel y Peajes'
  },
  others: {
    en: '$75 – $150 extra',
    es: '$75 – $150 adicionales'
  },
  us: {
    en: 'FREE (Included)',
    es: 'GRATIS (Incluido)'
  }
}, {
  charge: {
    en: 'Furniture Disassembly',
    es: 'Desarmado de Muebles'
  },
  others: {
    en: 'Charged per minute',
    es: 'Cobro por minuto extra'
  },
  us: {
    en: 'FREE (Included)',
    es: 'GRATIS (Incluido)'
  }
}, {
  charge: {
    en: 'Boxes & Tape',
    es: 'Cajas y Tapes'
  },
  others: {
    en: 'Sold at 3x markup',
    es: 'Te las venden al triple'
  },
  us: {
    en: 'INCLUDED (Free gift)',
    es: 'INCLUIDAS (De regalo)'
  }
}, {
  charge: {
    en: 'I-275 Traffic Delays',
    es: 'Tráfico en la I-275'
  },
  others: {
    en: 'Clock keeps running',
    es: 'El reloj sigue corriendo'
  },
  us: {
    en: "DOESN'T MATTER (Fixed Price)",
    es: 'NO IMPORTA (Precio Fijo)'
  }
}, {
  charge: {
    en: 'Long Walks / Elevators',
    es: 'Caminatas Largas / Elevadores'
  },
  others: {
    en: '$25 – $75 surcharge',
    es: '$25 – $75 de recargo'
  },
  us: {
    en: 'FREE (Included)',
    es: 'GRATIS (Incluido)'
  }
}];
export function ComparisonTable() {
  const {
    language
  } = useLanguage();
  const isEn = language === 'en';
  return <section className="py-20 bg-brand-black relative overflow-hidden border-t border-white/5" data-id="element-61">
      {/* Background accent */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-brand-red to-transparent opacity-30" data-id="element-62" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-63">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-14" data-id="element-64">
          <p className="text-brand-red font-oswald text-sm tracking-[0.3em] uppercase mb-3" data-id="element-65">
            {isEn ? 'The Prohibited Comparison' : 'La Comparativa Prohibida'}
          </p>
          <h2 className="font-oswald text-3xl md:text-5xl font-bold text-white uppercase mb-4" data-id="element-66">
            {isEn ? 'WHAT THEY HIDE vs. WHAT WE GIVE' : 'LO QUE ELLOS OCULTAN vs. LO QUE NOSOTROS DAMOS'}
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto" data-id="element-67">
            {isEn ? "Other Tampa movers lure you with a low hourly rate, then burn you with hidden charges. Here's the truth they don't want you to see." : 'Otras empresas te atrapan con un precio bajo "por hora" y luego te queman con cargos ocultos. Aquí está la verdad.'}
          </p>
        </motion.div>

        {/* Table */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.1
      }} className="rounded-2xl border border-white/10 overflow-hidden" data-id="element-68">
          {/* Table Header */}
          <div className="grid grid-cols-3 bg-white/[0.03]" data-id="element-69">
            <div className="px-4 md:px-6 py-4 border-b border-r border-white/10" data-id="element-70">
              <span className="font-oswald text-xs md:text-sm font-bold text-gray-500 uppercase tracking-wider" data-id="element-71">
                {isEn ? 'Extra Charge' : 'Cargo Extra'}
              </span>
            </div>
            <div className="px-4 md:px-6 py-4 border-b border-r border-white/10 text-center" data-id="element-72">
              <span className="font-oswald text-xs md:text-sm font-bold text-red-400 uppercase tracking-wider" data-id="element-73">
                <span className="inline-flex items-center gap-1.5" data-id="element-74">
                  <X className="w-3.5 h-3.5" strokeWidth={3} data-id="element-75" />
                  {isEn ? 'Others (Tampa)' : 'Otros (Tampa)'}
                </span>
              </span>
            </div>
            <div className="px-4 md:px-6 py-4 border-b border-white/10 text-center" data-id="element-76">
              <span className="font-oswald text-xs md:text-sm font-bold text-green-400 uppercase tracking-wider" data-id="element-77">
                <span className="inline-flex items-center gap-1.5" data-id="element-78">
                  <Check className="w-3.5 h-3.5" strokeWidth={3} data-id="element-79" />
                  Move Fighters
                </span>
              </span>
            </div>
          </div>

          {/* Table Rows */}
          {COMPARISONS.map((row, index) => <motion.div key={index} initial={{
          opacity: 0,
          x: -10
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.05
        }} className={`grid grid-cols-3 ${index < COMPARISONS.length - 1 ? 'border-b border-white/5' : ''} hover:bg-white/[0.02] transition-colors`} data-id="element-80">
              {/* Charge name */}
              <div className="px-4 md:px-6 py-4 border-r border-white/10 flex items-center" data-id="element-81">
                <span className="text-white text-sm md:text-base font-medium" data-id="element-82">
                  {isEn ? row.charge.en : row.charge.es}
                </span>
              </div>

              {/* Others */}
              <div className="px-4 md:px-6 py-4 border-r border-white/10 flex items-center justify-center text-center" data-id="element-83">
                <div className="flex items-center gap-2" data-id="element-84">
                  <X className="w-4 h-4 text-red-500 flex-shrink-0 hidden sm:block" strokeWidth={3} data-id="element-85" />
                  <span className="text-red-400 text-xs md:text-sm font-bold" data-id="element-86">
                    {isEn ? row.others.en : row.others.es}
                  </span>
                </div>
              </div>

              {/* Move Fighters */}
              <div className="px-4 md:px-6 py-4 flex items-center justify-center text-center bg-green-500/[0.03]" data-id="element-87">
                <div className="flex items-center gap-2" data-id="element-88">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0 hidden sm:block" strokeWidth={3} data-id="element-89" />
                  <span className="text-green-400 text-xs md:text-sm font-bold" data-id="element-90">
                    {isEn ? row.us.en : row.us.es}
                  </span>
                </div>
              </div>
            </motion.div>)}
        </motion.div>

        {/* Bottom callout */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.3
      }} className="mt-8 text-center" data-id="element-91">
          <p className="text-gray-500 text-sm" data-id="element-92">
            {isEn ? 'With Move Fighters, the price you see is the price you pay. Period.' : 'Con Move Fighters, el precio que ves es el precio que pagas. Punto.'}
          </p>
        </motion.div>
      </div>
    </section>;
}